import { Alert, Icon } from '@ver-uds/uswds-react';
import React, { useEffect, useState } from 'react';
import { PageLoader } from '@ver-uds/react';
import './DashboardLinkAccountsSection.scss';
import { CompanyName } from 'eve-account-client';
import { DashboardUser } from '@vis-auth/vis-user-client';
import DashboardLinkAccountsTable from './DashboardLinkAccountsTable/DashboardLinkAccountsTable';
import useAccountApi from '../../../services/AccountApi';
import { useDashboard } from '../../../context/Dashboard/DashboardContext';

export type DashboardLinkAccountsSectionProps = {
  setShowLinkModal: (show: boolean) => void;
  setLinkUserLogonID: (logonId: string) => void;
  setShowUnlinkModal: (show: boolean) => void;
  triggerPasswordReset: () => void;
  filteredLegacyUsersUnlinkedAccounts: DashboardUser[];
};

const DashboardLinkAccountsSection = ({
  setShowLinkModal,
  setLinkUserLogonID,
  setShowUnlinkModal,
  triggerPasswordReset,
  filteredLegacyUsersUnlinkedAccounts,
}: DashboardLinkAccountsSectionProps): JSX.Element => {
  const accountApi = useAccountApi();
  const { legacyUsers, hasUsersError, usersAreLoading } = useDashboard();
  const [companyNames, setCompanyNames] = useState<CompanyName[]>([]);
  const [showCompanyNameError, setShowCompanyNameError] = useState<boolean>(false);

  // TODO: Store the company names in a Map so that redundant companies do not require additional calls
  // Get company names asynchronously
  useEffect(() => {
    // Loop through user accounts
    legacyUsers.forEach((user) => {
      accountApi
        // Get company name for each user account
        .getCompanyNameByAccountId({ accountId: user.accountId })
        .then((companyName) => {
          // Add company name if get company name call succeeds
          setCompanyNames((prev) => prev.concat(companyName));
        })
        .catch(() => {
          // Show error if get company name call fails
          setShowCompanyNameError(true);
        });
    });
  }, [accountApi, legacyUsers, setCompanyNames, setShowCompanyNameError]);

  return (
    <div className={PageLoader.sectionParentClassname}>
      {usersAreLoading && <PageLoader label="Loading" sectionOverlay />}
      <h2 className="margin-top-4">{`${filteredLegacyUsersUnlinkedAccounts.length} Existing User Account${
        legacyUsers.length === 1 ? '' : 's'
      } Found`}</h2>

      {/* Error when get company name call failed */}
      {showCompanyNameError && (
        <Alert status="warning" slim className="margin-top-3 margin-bottom-3">
          <Alert.Text>We could not return all information for these user accounts at this time.</Alert.Text>
        </Alert>
      )}

      {/* Error when get dashboard users call failed */}
      {hasUsersError && (
        <Alert status="warning" slim className="margin-top-3 margin-bottom-3">
          <Alert.Text>User account information could not be displayed at this time.</Alert.Text>
        </Alert>
      )}

      {/* Has at least one legacy user */}
      {legacyUsers.length >= 1 && (
        <Alert status="info" slim className="margin-top-3 margin-bottom-3">
          <Alert.Text>There are user accounts that have the same email address as your Login.gov account.</Alert.Text>
        </Alert>
      )}

      <div className="grid-row margin-top-2 user-accounts-body-container">
        <p className="tablet:grid-col-9">
          After linking a user account, it will be accessible in the User Accounts table above. If an existing user
          account does not appear below, use the &quot;Link User Account&quot; button.
        </p>
      </div>

      <DashboardLinkAccountsTable
        companyNames={companyNames}
        setShowLinkModal={setShowLinkModal}
        setLinkUserLogonID={setLinkUserLogonID}
        setShowUnlinkModal={setShowUnlinkModal}
        triggerPasswordReset={triggerPasswordReset}
        filteredLegacyUsersUnlinkedAccounts={filteredLegacyUsersUnlinkedAccounts}
      />
    </div>
  );
};

export default DashboardLinkAccountsSection;
